import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { EmbeddedPodlet, type EmbeddedPodlets } from "@mobility-item-page/client";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useLatestAds, useOrganisation } from "src/context/DealerDataProviderContext.tsx";
import { trackPageView } from "src/utils/pulseHelper.ts";
import type { PodletsNames, AppProps as ServerAppProps } from "types/app.ts";
import "./app.css";
import ErrorBoundary from "./client/Components/ErrorBoundary.tsx";
import { NavigationBar } from "./client/Components/NavigationBar.tsx";
import { AboutUs } from "./client/Sections/AboutUs.tsx";
import { Campaigns } from "./client/Sections/Campaigns.tsx";
import { DealerAds } from "./client/Sections/DealerAds.tsx";
import { Employees } from "./client/Sections/Employees.tsx";
import { ImageGallery } from "./client/Sections/ImageGallery.tsx";
import { ImageGalleryFullscreen } from "./client/Sections/ImageGalleryFullscreen.tsx";
import { OtherLocations } from "./client/Sections/OtherLocations.tsx";
import { AppConfigProvider, useAppConfig } from "./context/AppConfigProviderContext.tsx";
import { OrganisationProvider } from "./context/DealerDataProviderContext.tsx";
import { GalleryStateProvider, useGalleryState } from "./context/GalleryStateProviderContext.tsx";
import { TabId } from "./utils/tabs.ts";

type AppProps = ServerAppProps;

const App = (props: AppProps) => {
  return (
    <Providers {...props}>
      <AppContent broadcast={props.podlets.broadcast} />
    </Providers>
  );
};

type ProviderProps = ServerAppProps & {
  children?: ReactNode;
};

const Providers = ({
  appConfig,
  organisation,
  otherLocations,
  latestAds,
  imageGallery,
  latestDealerGroupAds,
  salesPersons,
  children,
}: ProviderProps) => {
  return (
    <I18nProvider i18n={i18n}>
      <AppConfigProvider appConfig={appConfig}>
        <ErrorBoundary helpCenter={appConfig.helpCenter}>
          <OrganisationProvider
            organisation={organisation}
            otherLocations={otherLocations}
            latestAds={latestAds}
            images={imageGallery}
            latestGroupAds={latestDealerGroupAds}
            salesPersons={salesPersons}
          >
            <GalleryStateProvider>{children}</GalleryStateProvider>
          </OrganisationProvider>
        </ErrorBoundary>
      </AppConfigProvider>
    </I18nProvider>
  );
};

const AppContent = (podlets: EmbeddedPodlets<PodletsNames>) => {
  const { tabs } = useAppConfig();
  const { name, organisationId, coverImageUrl } = useOrganisation();
  const latestAds = useLatestAds();
  const { fullScreen } = useGalleryState();
  const [activeTab, setActiveTab] = useState<TabId | undefined>(undefined);

  const aboutUsRef = useRef<HTMLDivElement | null>(null);
  const campaignsRef = useRef<HTMLDivElement | null>(null);
  const dealerAdsRef = useRef<HTMLDivElement | null>(null);
  const imageGalleryRef = useRef<HTMLDivElement | null>(null);
  const employeesRef = useRef<HTMLDivElement | null>(null);
  const departmentsRef = useRef<HTMLDivElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only track page view on load
  useEffect(() => {
    trackPageView(name, organisationId);
  }, []);

  useEffect(() => {
    const sectionMap: Record<string, TabId> = {};
    for (const t of tabs) sectionMap[t] = t;

    const intersectionState = new Map<string, IntersectionObserverEntry>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) intersectionState.set(entry.target.id, entry);
        const visibleEntries = Array.from(intersectionState.values())
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        const topVisible = visibleEntries[0];
        if (topVisible?.target.id) {
          const nextTab = sectionMap[topVisible.target.id];
          if (nextTab) {
            setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
          }
        }
      },
      { threshold: [0.25, 0.5, 0.75] },
    );

    const refs = [aboutUsRef, campaignsRef, dealerAdsRef, imageGalleryRef, employeesRef, departmentsRef];

    for (const ref of refs) {
      if (ref.current) observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [tabs]);

  return (
    <>
      {coverImageUrl && (
        <div className="md:mb-40 mb-10">
          <img
            src={coverImageUrl}
            alt={`${name} cover`}
            className="w-full max-h-[150] md:max-h-[360] object-cover block"
          />
        </div>
      )}
      <NavigationBar activeTab={activeTab ?? TabId.AboutUs} setActiveTab={setActiveTab} />
      {fullScreen && tabs.includes(TabId.ImageGallery) && <ImageGalleryFullscreen />}
      <main
        // prevent FOUCE on w-icon: https://pages.github.schibsted.io/nmp-web/borealis-docs/news/fixing-cls-and-fouce/
        data-await-elements="w-icon"
        className={`page-container flex flex-col gap-40 mb-40 md:mt-40 mt-28`}
      >
        <EmbeddedPodlet response={podlets.broadcast} cautiouslyReloadScriptOnHydrationError suppressHydrationWarning />
        {tabs.includes(TabId.AboutUs) && <AboutUs ref={aboutUsRef} />}
        {tabs.includes(TabId.Campaigns) && <Campaigns ref={campaignsRef} />}
        {tabs.includes(TabId.DealerAds) && <DealerAds ref={dealerAdsRef} ads={latestAds} />}
        {tabs.includes(TabId.ImageGallery) && <ImageGallery ref={imageGalleryRef} />}
        {tabs.includes(TabId.Employees) && <Employees ref={employeesRef} />}
        {tabs.includes(TabId.OtherLocations) && <OtherLocations ref={departmentsRef} />}
      </main>
    </>
  );
};

export default App;
